import { Sidebar } from './Sidebar';
import { useLocation, Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { Menu } from 'lucide-react';
import { NotificationBell } from '../notifications/NotificationBell';

interface DashboardLayoutProps {
    children: React.ReactNode;
}

const ROUTE_LABELS: Record<string, string> = {
    '/new': 'New Quote',
    '/quotes': 'Quotes History',
    '/orders': 'Orders',
    '/parts': 'Parts Library',
    '/admin': 'Administration',
    '/admin/drafts': 'Quote Drafts',
    '/admin/customers': 'Customers',
    '/admin/bulk-import': 'Bulk Cost Import',
    '/production': 'Production Management',
    '/account': 'Account Settings'
};

export function DashboardLayout({ children }: DashboardLayoutProps) {
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        return localStorage.getItem('sidebar-collapsed') === 'true';
    });

    const toggleSidebarCollapse = () => {
        setIsSidebarCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('sidebar-collapsed', String(next));
            return next;
        });
    };

    const breadcrumbs = useMemo(() => {
        const path = location.pathname;
        if (path === '/new') return ['Quotes', 'New Quote'];
        if (path === '/quotes') return ['Quotes'];
        return [ROUTE_LABELS[path] || 'Dashboard'];
    }, [location.pathname]);

    return (
        <div className="flex min-h-[100dvh] bg-slate-50">
            <Sidebar 
                isOpen={isSidebarOpen} 
                onClose={() => setIsSidebarOpen(false)} 
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={toggleSidebarCollapse}
            />

            <main className="flex-1 min-w-0 overflow-hidden flex flex-col h-[100dvh]">
                {/* Mobile Header */}
                <header className="lg:hidden h-16 bg-sidebar border-b border-sidebar-accent flex items-center justify-between px-4 shrink-0">
                    <Link to="/" className="flex items-center">
                        <img src="/whiteout-logo.png" alt="Print My Parts" className="h-6 w-auto" />
                    </Link>
                    <button
                        onClick={() => setIsSidebarOpen(true)}
                        className="p-2 text-slate-400 hover:text-white transition-colors"
                    >
                        <Menu size={24} />
                    </button>
                </header>

                {location.pathname !== '/new' && (
                    <header className="h-16 bg-white border-b flex items-center justify-between px-4 sm:px-8 sticky top-0 z-30 shrink-0">
                        <div className="flex items-center gap-2 text-[10px] sm:text-sm overflow-hidden">
                            {breadcrumbs.map((crumb, i) => (
                                <div key={crumb} className="flex items-center gap-2 whitespace-nowrap">
                                    {i > 0 && <span className="text-slate-300">/</span>}
                                    <span className={cn(
                                        i === breadcrumbs.length - 1 ? "text-slate-900 font-bold" : "text-slate-500 font-medium",
                                        i < breadcrumbs.length - 1 && "hidden sm:inline" // Hide previous breadcrumbs on tiny mobile
                                    )}>
                                        {crumb}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center gap-4 ml-auto">
                            <NotificationBell />
                        </div>
                    </header>
                )}

                <div className={cn("flex-1 overflow-auto", location.pathname === '/new' ? "" : "p-4 md:p-6 lg:p-8")} key={location.pathname}>
                    {children}
                </div>
            </main>
        </div>
    );
}

import { cn } from '../../lib/utils';

